import { Database } from 'types/supabase'
import { supabaseClient } from 'supabase'
import { uploadImage } from 'supabase/files'

export type CreativeDetailRow = {
  icon: string | null
  main: string
  sub: string | null
  // PT variants (per-row, stored inside the details jsonb)
  main_pt: string | null
  sub_pt: string | null
}

export type CreateCreativeParams = {
  advertiser_id: Database['public']['Tables']['creatives']['Row']['advertiser_id']
  title?: Database['public']['Tables']['creatives']['Row']['title']
  description?: Database['public']['Tables']['creatives']['Row']['description']
  cta_text?: Database['public']['Tables']['creatives']['Row']['cta_text']
  cta_link?: Database['public']['Tables']['creatives']['Row']['cta_link']
  // Which advertiser contact the mobile "Book here" CTA opens
  cta_channel?: Database['public']['Tables']['creatives']['Row']['cta_channel']
  // 1:1 square shown on the in-feed ad card
  card_image_file: File
  // 16:9 wide shown in the ad detail hero
  detail_image_file: File
  // MVP-7 detail-page content (all optional)
  description_pt?: string | null
  price_from?: number | null
  price_currency?: string | null
  price_caption?: string | null
  highlights?: string[] | null
  details?: CreativeDetailRow[] | null
  location_label?: string | null
  location_lat?: number | null
  location_long?: number | null
  badge_label?: string | null
  advertiser_caption?: string | null
  // Short city name shown on the in-feed ad card
  city?: string | null
  // Bilingual: PT variants of the content fields
  title_pt?: string | null
  cta_text_pt?: string | null
  price_caption_pt?: string | null
  highlights_pt?: string[] | null
  badge_label_pt?: string | null
  advertiser_caption_pt?: string | null
  // Viewable via the in-app preview deep link (not shown in the feed)
  is_preview?: boolean
  // Promo/discount code shown on the mobile detail page (toggle + code + caption)
  promo_enabled?: boolean
  promo_code?: string | null
  promo_caption?: string | null
  promo_caption_pt?: string | null
}

export const createCreative = async ({
  advertiser_id,
  title,
  description,
  cta_text,
  cta_link,
  cta_channel,
  card_image_file,
  detail_image_file,
  description_pt,
  price_from,
  price_currency,
  price_caption,
  highlights,
  details,
  location_label,
  location_lat,
  location_long,
  badge_label,
  advertiser_caption,
  city,
  title_pt,
  cta_text_pt,
  price_caption_pt,
  highlights_pt,
  badge_label_pt,
  advertiser_caption_pt,
  is_preview,
  promo_enabled,
  promo_code,
  promo_caption,
  promo_caption_pt,
}: CreateCreativeParams) => {
  // Upload both images (crop enforces the ratios client-side, so no validation here).
  const cardImageUrl = await uploadImage(
    card_image_file,
    `${Date.now()}-card.${card_image_file.type.split('/')[1]}`,
    'creatives',
    advertiser_id
  )

  if (!cardImageUrl) {
    throw new Error('Failed to upload card image')
  }

  const detailImageUrl = await uploadImage(
    detail_image_file,
    `${Date.now()}-detail.${detail_image_file.type.split('/')[1]}`,
    'creatives',
    advertiser_id
  )

  if (!detailImageUrl) {
    throw new Error('Failed to upload detail image')
  }

  const newCreative = {
    advertiser_id,
    title,
    description,
    cta_text,
    cta_link,
    cta_channel,
    card_image_url: cardImageUrl,
    detail_image_url: detailImageUrl,
    description_pt,
    price_from,
    price_currency,
    price_caption,
    highlights,
    details,
    location_label,
    location_lat,
    location_long,
    badge_label,
    advertiser_caption,
    city,
    title_pt,
    cta_text_pt,
    price_caption_pt,
    highlights_pt,
    badge_label_pt,
    advertiser_caption_pt,
    is_preview,
    promo_enabled,
    promo_code,
    promo_caption,
    promo_caption_pt,
  }

  const { error, data } = await supabaseClient
    .from('creatives')
    .insert(newCreative)
    .select()

  if (error) {
    throw error
  }

  return data[0]
}